import { NavigationFeedback } from './shared/navigation';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { lazy, Suspense, useMemo, type ReactNode } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { store } from './app/store';
import { bootstrapSession } from './app/sessionSlice';
import { createPlatformTheme } from './app/theme';
import { ErrorBoundary, FailureScreen } from './app/ErrorBoundary';
import { i18nextInit } from './main/initializers/i18n';
import { AppThemeProvider, isDarkTheme, useAppTheme } from './main/theme/AppThemeContext';
import App from './app/App';

// Shown only when an atech-chat app key is configured (ATech Chat/03 §9); the dynamic import keeps
// the chat out of builds without one.
const ATechChat = import.meta.env.VITE_ATECH_CHAT_APP_KEY ? lazy(() => import('./shared/atech-chat')) : null;
import './main/theme/theme.css';
import './styles.css';
import './premium/workspace.css';
import './shared/preferences/themes.css';
import { configurePreferences } from './shared/preferences';
import { config } from './core/config';
import { tokenStorage } from './core/auth/tokenStorage';

configurePreferences({ platformApi: config.apiBaseUrl, getAccessToken: () => tokenStorage.getAccessToken() });

const container = document.getElementById('root') as HTMLElement;

// Restore (or discard) any previous session before first paint of protected UI.
void store.dispatch(bootstrapSession());

/** One theme for every area (public landing, customer hub, workspace); light by default. */
function ThemedRoot({ children }: { children: ReactNode }) {
  const { theme } = useAppTheme();
  const muiTheme = useMemo(() => createPlatformTheme(theme), [theme]);
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
      <ToastContainer position="bottom-center" newestOnTop closeOnClick theme={isDarkTheme(theme) ? 'dark' : 'light'} />
    </ThemeProvider>
  );
}

function renderApp() {
  // Mount once: StrictMode replays page effects and their API requests in development.
  createRoot(container).render(
    <Provider store={store}>
      <AppThemeProvider>
      <ThemedRoot>
        {/*
          Outside the router and outside every provider, so it also catches a throw from the
          session or the ported redux bridge. Without it a render error unmounts the whole
          tree and the operator gets an unexplained white page.
        */}
        <ErrorBoundary title="Platform could not start">
          <BrowserRouter><NavigationFeedback resolveDestinations />
            <App />
            {ATechChat && <Suspense fallback={null}><ATechChat /></Suspense>}
          </BrowserRouter>
        </ErrorBoundary>
      </ThemedRoot>
      </AppThemeProvider>
    </Provider>,
  );
}

/*
 * i18next MUST be initialised before the tree mounts, and that ordering is the whole point of
 * this block.
 *
 * The ported PoseidonFrontend tree does not tolerate an uninitialised i18n: its theme provider
 * (`main/theme/AppThemeContext.tsx`) calls `i18n.on('languageChanged', ...)`, and when
 * react-i18next has no initialised instance to hand out, `useTranslation()` gives it a stub
 * whose `on` is not a function. That threw `TypeError: i18n.on is not a function` during the
 * first render of every ported screen.
 *
 * It is worse than a one-off failure: the bootstrap used to live in a `LegacyRuntime` effect,
 * and React flushes child effects BEFORE parent effects. The theme provider is the child, so it
 * threw before the parent's effect ran — which means the bootstrap never started, so i18n never
 * became initialised and *every* navigation into a ported screen failed the same way for the
 * rest of the page load. `/app` (the MUI shell, no ported theme provider) rendered fine, which
 * is why the app looked healthy until you clicked something.
 *
 * RestaurantFE awaits the same bootstrap before mounting for the same reason. A rejected locale
 * fetch is reported instead of leaving `#root` empty.
 */
void i18nextInit()
  .then(renderApp)
  .catch((error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('i18n initialization failed before the app mounted:', error);
    createRoot(container).render(<FailureScreen title="Platform could not start" error={error} />);
  });
